import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { delay, Observable, of, throwError } from 'rxjs';
import {
  FaxCoverPreviewPayload,
  FaxDirection,
  FaxErrorCode,
  FaxFailureReason,
  FaxOriginals,
  FaxRecord,
  FaxRecordStatus,
  GetFaxesQuery,
  Page,
  ProviderFaxLine,
  ProviderFaxLineStatus,
  ProviderFaxLineStatusResult,
  ProviderFaxLineVendorState,
  SendFaxPayload,
} from '@models';
import { FaxContent, FaxService } from './fax.service';

const ACTIVATION_DELAY_MS = 2500;

const LIST_DELAY_MS = 400;

@Injectable({ providedIn: 'root' })
export class FaxMockService implements FaxService {
  private readonly line = signal<ProviderFaxLine | null>(null);
  private readonly faxes = signal<FaxRecord[]>(seedFaxes());
  private readonly retriedOriginals = new Set<string>();

  getMyLine(): Observable<ProviderFaxLine> {
    const line = this.line();
    return line ? of(line) : throwError(() => this.notFound());
  }

  activateLine(areaCode?: string): Observable<ProviderFaxLine> {
    const now = new Date().toISOString();
    const activated: ProviderFaxLine = {
      id: 'mock-fax-line-id',
      phoneNumber: areaCode ? `+1 (${areaCode}) 555-0142` : '+1 (415) 555-0198',
      status: ProviderFaxLineStatus.Active,
      createdAtUtc: this.line()?.createdAtUtc ?? now,
      provisionedAtUtc: now,
    };

    this.line.set(activated);
    return of(activated).pipe(delay(ACTIVATION_DELAY_MS));
  }

  getLineStatus(): Observable<ProviderFaxLineStatusResult> {
    const line = this.line();
    if (!line) {
      return throwError(() => this.notFound());
    }

    return of({
      line,
      vendorState: ProviderFaxLineVendorState.NumberBoundToService,
      inboundWebhookConfiguredAtVendor: true,
      agrees: line.status === ProviderFaxLineStatus.Active,
    });
  }

  releaseLine(): Observable<ProviderFaxLine> {
    const line = this.line();
    if (!line) {
      return throwError(() => this.notFound());
    }

    const released: ProviderFaxLine = {
      ...line,
      status: ProviderFaxLineStatus.Released,
      releasedAtUtc: new Date().toISOString(),
    };

    this.line.set(released);
    return of(released);
  }

  getFaxes(query: GetFaxesQuery): Observable<Page<FaxRecord>> {
    const direction = query.direction ?? FaxDirection.Inbound;
    const search = query.search?.trim().toLowerCase();

    const matches = this.faxes()
      .filter(fax => fax.direction === direction)
      .filter(fax => !search || describesFax(fax).includes(search));

    const pageNumber = query.pageNumber ?? 1;
    const pageSize = query.pageSize ?? 25;
    const start = (pageNumber - 1) * pageSize;

    return of({
      items: matches.slice(start, start + pageSize),
      totalItems: matches.length,
      pageNumber,
      pageSize,
      totalPages: Math.ceil(matches.length / pageSize),
    }).pipe(delay(LIST_DELAY_MS));
  }

  getFax(id: string): Observable<FaxRecord> {
    const fax = this.faxes().find(candidate => candidate.id === id);
    return fax ? of(fax) : throwError(() => this.faxNotFound());
  }

  getFaxContent(id: string): Observable<FaxContent> {
    const fax = this.faxes().find(candidate => candidate.id === id);
    if (!fax) {
      return throwError(() => this.faxNotFound());
    }

    const blob = new Blob([`Mock fax ${id}`], { type: 'text/plain' });
    return of({ blob, fileName: `fax-${id}.txt` }).pipe(delay(LIST_DELAY_MS));
  }

  getFaxOriginals(id: string): Observable<FaxOriginals> {
    const originals = MOCK_ORIGINALS[id];
    if (!originals) {
      return throwError(() => this.faxNotFound());
    }

    // The first request for this fax fails once so the retry path can be exercised.
    if (id === 'mock-fax-7' && !this.retriedOriginals.has(id)) {
      this.retriedOriginals.add(id);
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 503,
            error: {
              code: 'FAX_ORIGINALS_TEMPORARILY_UNAVAILABLE',
              message:
                "We couldn't load the original attachments right now. Try again",
            },
          })
      ).pipe(delay(LIST_DELAY_MS));
    }

    return of(originals).pipe(delay(LIST_DELAY_MS));
  }

  getFaxOriginalContent(id: string, position: number): Observable<FaxContent> {
    const original = MOCK_ORIGINALS[id]?.files.find(
      file => file.position === position
    );
    if (!original) {
      return throwError(() => this.faxNotFound());
    }

    const blob = new Blob([`Mock original ${original.fileName}`], {
      type: 'text/plain',
    });
    return of({ blob, fileName: original.fileName }).pipe(delay(LIST_DELAY_MS));
  }

  markFaxRead(id: string): Observable<FaxRecord> {
    return this.update(id, fax => ({ ...fax, isRead: true }));
  }

  attachFaxToPatient(id: string, patientId: string): Observable<FaxRecord> {
    return this.update(id, fax => ({
      ...fax,
      filedPatientId: patientId,
      filedPatientName: MOCK_PATIENT_NAMES[patientId] ?? 'Bonnie Green',
      filedDocumentId: `mock-document-${id}`,
      filedAtUtc: new Date().toISOString(),
    }));
  }

  sendFax(payload: SendFaxPayload): Observable<FaxRecord> {
    const sent: FaxRecord = {
      id: `mock-fax-${Date.now()}`,
      direction: FaxDirection.Outbound,
      fromNumber: this.line()?.phoneNumber ?? '+14155550198',
      toNumber: payload.toNumber,
      pageCount: 1 + (payload.files?.length ?? 0),
      status: FaxRecordStatus.Submitting,
      subject: payload.subject,
      receivedAtUtc: new Date().toISOString(),
      isRead: true,
      hasContent: false,
      recipientName: payload.recipientName,
      recipientOrganization: payload.recipientOrganization,
      reasonToFax: payload.reason,
      sentForPatientId: payload.patientId,
      canResend: false,
    };

    this.faxes.update(faxes => [sent, ...faxes]);

    return of(sent).pipe(delay(LIST_DELAY_MS));
  }

  resendFax(id: string): Observable<FaxRecord> {
    return this.update(id, fax => ({
      ...fax,
      status: FaxRecordStatus.Queued,
      failureReason: undefined,
      canResend: false,
    }));
  }

  previewFaxCover(payload: FaxCoverPreviewPayload): Observable<Blob> {
    return throwError(() => this.coverUnavailable(payload));
  }

  private coverUnavailable(payload: FaxCoverPreviewPayload): HttpErrorResponse {
    return new HttpErrorResponse({
      status: 501,
      error: {
        code: FaxErrorCode.Unavailable,
        message: `Cover previews are not rendered in mock mode (${payload.attachmentPageCount ?? 0} attachment pages)`,
      },
    });
  }

  private update(
    id: string,
    change: (fax: FaxRecord) => FaxRecord
  ): Observable<FaxRecord> {
    const existing = this.faxes().find(candidate => candidate.id === id);
    if (!existing) {
      return throwError(() => this.faxNotFound());
    }

    const updated = change(existing);
    this.faxes.update(faxes =>
      faxes.map(fax => (fax.id === id ? updated : fax))
    );

    return of(updated).pipe(delay(LIST_DELAY_MS));
  }

  private notFound(): HttpErrorResponse {
    return new HttpErrorResponse({
      status: 404,
      error: {
        code: FaxErrorCode.LineNotFound,
        message: 'No fax line has been activated for this provider',
      },
    });
  }

  private faxNotFound(): HttpErrorResponse {
    return new HttpErrorResponse({
      status: 404,
      error: { code: 'FAX_NOT_FOUND', message: 'Fax not found' },
    });
  }
}

const MOCK_ORIGINALS: Record<string, FaxOriginals> = {
  'mock-fax-5': {
    faxId: 'mock-fax-5',
    expectedCount: 3,
    missingCount: 1,
    files: [
      {
        position: 1,
        fileName: 'Lab results.pdf',
        contentType: 'application/pdf',
        canPreview: true,
        pageCount: 2,
      },
      {
        position: 2,
        fileName: 'Consent form.tif',
        contentType: 'image/tiff',
        canPreview: false,
        pageCount: null,
      },
    ],
  },
  'mock-fax-6': {
    faxId: 'mock-fax-6',
    expectedCount: 2,
    missingCount: 2,
    files: [],
  },
  'mock-fax-7': {
    faxId: 'mock-fax-7',
    expectedCount: 1,
    missingCount: 0,
    files: [
      {
        position: 1,
        fileName: 'Supporting image.png',
        contentType: 'image/png',
        canPreview: true,
        pageCount: 1,
      },
    ],
  },
};

const MOCK_PATIENT_NAMES: Record<string, string> = {
  'mock-patient-1': 'Bonnie Green',
  'mock-patient-2': 'Micheal Gough',
  'mock-patient-3': 'Karen Nelson',
};

function describesFax(fax: FaxRecord): string {
  return [fax.fromNumber, fax.subject, `${fax.pageCount}`]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function hoursAgo(hours: number): string {
  return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
}

function seedFaxes(): FaxRecord[] {
  return [
    {
      id: 'mock-fax-1',
      direction: FaxDirection.Inbound,
      fromNumber: '+14155550142',
      toNumber: '+14155550198',
      pageCount: 3,
      status: FaxRecordStatus.Completed,
      subject: 'Comprehensive metabolic panel',
      receivedAtUtc: hoursAgo(2),
      isRead: false,
      hasContent: true,
    },
    {
      id: 'mock-fax-2',
      direction: FaxDirection.Inbound,
      fromNumber: '+15105550164',
      toNumber: '+14155550198',
      pageCount: 1,
      status: FaxRecordStatus.Completed,
      receivedAtUtc: hoursAgo(6),
      isRead: false,
      hasContent: true,
    },
    {
      id: 'mock-fax-3',
      direction: FaxDirection.Inbound,
      fromNumber: '+14155550175',
      toNumber: '+14155550198',
      pageCount: 4,
      status: FaxRecordStatus.Completed,
      subject: 'MRI report — lumbar spine',
      receivedAtUtc: hoursAgo(30),
      isRead: true,
      hasContent: true,
      filedDocumentId: 'mock-document-3',
      filedPatientId: 'mock-patient-1',
      filedPatientName: 'Bonnie Green',
      filedAtUtc: hoursAgo(29),
    },
    {
      id: 'mock-fax-4',
      direction: FaxDirection.Inbound,
      fromNumber: '+19255550109',
      toNumber: '+14155550198',
      pageCount: 6,
      status: FaxRecordStatus.Completed,
      subject: 'Transfer of care summary',
      receivedAtUtc: hoursAgo(120),
      isRead: true,
      hasContent: false,
    },
    {
      id: 'mock-fax-5',
      direction: FaxDirection.Outbound,
      fromNumber: '+14155550198',
      toNumber: '+16285550117',
      pageCount: 4,
      status: FaxRecordStatus.Failure,
      subject: 'Lab results',
      receivedAtUtc: hoursAgo(20),
      isRead: true,
      hasContent: false,
      recipientName: 'Dr. Amelia Patel',
      failureReason: FaxFailureReason.Call,
      canResend: true,
      attachmentCount: 3,
    },
    {
      id: 'mock-fax-6',
      direction: FaxDirection.Outbound,
      fromNumber: '+14155550198',
      toNumber: '+19255550109',
      pageCount: 3,
      status: FaxRecordStatus.Failure,
      subject: 'Consent form',
      receivedAtUtc: hoursAgo(26),
      isRead: true,
      hasContent: false,
      failureReason: FaxFailureReason.Fax,
      canResend: true,
      attachmentCount: 2,
    },
    {
      id: 'mock-fax-7',
      direction: FaxDirection.Outbound,
      fromNumber: '+14155550198',
      toNumber: '+15105550164',
      pageCount: 2,
      status: FaxRecordStatus.Failure,
      receivedAtUtc: hoursAgo(50),
      isRead: true,
      hasContent: false,
      failureReason: FaxFailureReason.General,
      canResend: true,
      attachmentCount: 1,
    },
    {
      id: 'mock-fax-8',
      direction: FaxDirection.Outbound,
      fromNumber: '+14155550198',
      toNumber: '+14155550142',
      pageCount: 1,
      status: FaxRecordStatus.Failure,
      subject: 'Note only',
      receivedAtUtc: hoursAgo(70),
      isRead: true,
      hasContent: false,
      failureReason: FaxFailureReason.Call,
      canResend: true,
      attachmentCount: 0,
    },
  ];
}
